import React from "react";
import "./About.css";
import { Paper } from "@mui/material";
import groupingImage from "../../images/about/grouping.png";
import figure2 from "../../images/about/Figure2.png";
import figure3 from "../../images/about/Figure3.png";

export default function About() {
  return (
    <div className="about--container">
      <header className="about--header">
        <h1>About</h1>
      </header>
      <div className="about--content">
        <h2>Background</h2>
        <p>
          Orchestration is known as the art of combining instruments through
          various techniques to achieve a particular sonic goal. However, the
          practice of orchestration has not been studied systematically with the
          aim of developing a theory of the conditions under which certain
          orchestration techniques work, and how they are perceived.
        </p>
        <p>
          The aim of the <strong>Orchestration and Perception Project</strong>{" "}
          and subsequently the Analysis, Creation, and Teaching of Orchestration
          (ACTOR Project) was to develop a psychological foundation for a theory
          of orchestration practice based on perceptual principles associated
          with musical timbre. In this ongoing research program, we are studying
          implicit theory contained within orchestration treatises and
          orchestral scores. For more information about the larger research
          program, see our{" "}
          <a
            href="https://timbreandorchestration.org"
            target="_blank"
            rel="noopener noreferrer"
            className="about--link"
          >
            website
          </a>
          .
        </p>
        <p>
          We discovered that many orchestration aims are related to{" "}
          <strong>auditory grouping processes</strong> (McAdams & Bregman, 1979;
          McAdams, 2019), which organize the acoustic environment and determine
          what sounds are grouped together into musical events in concurrent
          grouping, whether these events are connected into musical streams in
          sequential grouping, and how listeners chunk event streams into
          musical units such as motifs, phrases, or themes in segmental grouping
          (as shown in Figure 1).
        </p>
        <br />
        <Paper className="about--paper">
          <h3 className="about--title">
            Figure 1 - Orchestral effects and auditory grouping processes (from
            Goodchild & McAdams, 2021)
          </h3>
          <img
            src={groupingImage}
            alt="Orchestral effects and auditory grouping processes"
          />
        </Paper>
        <br />
        <p>
          The OrchARD website presents annotations of orchestral scores based on
          listening to specific performances. it currently contains a mixture of
          two separate taxonomies that organize the grouping processes into
          analytical categories. We developed a first data model whose taxonomy
          is shown in Figure 2. Most of the annotated pieces appear in this
          form. annotated pieces appear in this form. The annotation scheme for
          Data Model 1 can be viewed{" "}
          <a className="about--link" href="/first-data-model">
            here
          </a>
          .
        </p>
        <br />
        <Paper className="about--paper">
          <h3 className="about--title">
            Figure 2 - Taxonomy implemented in Data Model 1
          </h3>
          <img src={figure2} alt="Taxonomy implemented in Data Model 1" />
        </Paper>
        <br />
        <p>
          After significant experience using this taxonomy and making necessary
          adjustments, Data Model 2 was implemented based on the paper by
          McAdams, Goodchild & Soden (2022). The taxonomy for this data model
          has been updated and is shown in Figure 3. The annotation scheme for
          Data Model 2 is explained{" "}
          <a
            href="https://timbreandorchestration.org/tor/modules/taxonomy/orchestral-grouping-effects/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className="about--link"
          >
            here
          </a>
          .
        </p>
        <br />
        <Paper className="about--paper">
          <h3 className="about--title">
            Figure 3 - Taxonomy implemented in Data Model 2
          </h3>
          <img src={figure3} alt="Taxonomy implemented in Data Model 2" />
        </Paper>
        <br />
        <h2>Note about scores and recordings</h2>
        <ul>
          <li>
            All of the scores displayed on the detail pages either originated
            from IMSLP and are in the public domain in Canada or were created by
            OrchPlayMusic for the ACTOR project in xml. We do not assume any
            legal responsibility or liability for the consequences of
            downloading files that are not in the public domain in your country.
          </li>
          <br />
          <li>
            The short sound clips were extracted from commercial recordings and
            they are made available for the purposes of research, education and
            private study under the "fair dealing" provision of the Canadian
            Copyright Act.
          </li>
          <br />
          <li>For more information, please contact our project manager.</li>
        </ul>
        <br />
        <Paper className="about--paper">
          <h2 className="about--title" style={{ textAlign: "center" }}>
            References
          </h2>
          <p>
            McAdams, S. (2019). Timbre as a structuring force in music. In K.
            Siedenburg, C. Saitis, S. McAdams, A. Popper, A. & R. Fay (Eds.),
            Timbre: Acoustics, Perception, and Cognition (pp. 211-244). Springer
            Handbook of Auditory Research, Springer Verlag, Cham, Switzerland.
            https://doi.org/10.1007/978-3-030-14832-4_8
          </p>
          <p>
            McAdams, S. & Bregman, A. (1979). Hearing musical streams. Computer
            Music Journal, 3(4), 26-43, republished in C. Roads & J. Strawn
            (Eds.) (1985), The Foundations of Computer Music (pp. 658-698),
            Cambridge, MA: MIT Press. https://www.jstor.org/stable/4617866{" "}
          </p>
          <p>
            Goodchild, M. & McAdams, S. (2021). Perceptual processes in
            orchestration. In E. I. Dolan & A. Rehding (Eds.), The Oxford
            Handbook of Timbre (pp. 497-524). Oxford University Press, New York,
            NY. https://doi.org/10.1093/oxfordhb/9780190637224.013.10
          </p>
          <p>
            McAdams, S., Goodchild, M. & Soden, K. (2022). A taxonomy of
            orchestral grouping effects derived from principles of auditory
            perception. Music Theory Online, 28(3).
            https://doi.org/10.30535/mto.28.3.6
          </p>
        </Paper>
      </div>
    </div>
  );
}
